import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Check, FlaskConical, Leaf, ShoppingBag, Timer, WheatOff } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { PRODUCTS } from "@/data/content";

function BundleCard() {
  const { products, addItems, busy } = useCart();
  const v = products.vanilla;
  const c = products.chocolate;
  const total = v && c ? Number(v.price) + Number(c.price) : null;
  const bundlePrice = total !== null ? Math.round(total * 0.9) : null;

  return (
    <Reveal>
      <div
        data-testid="bundle-card"
        className="mt-8 rounded-[2.5rem] bg-[#2C221E] text-[#FAF7F2] overflow-hidden relative"
      >
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#6A2C3E]/50 blur-[90px]" />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#2A524A]/40 blur-[90px]" />
        <div className="relative grid md:grid-cols-2 gap-8 items-center p-8 sm:p-12">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#C86D51] mb-4">Bundle & Bake</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">
              Buy 2 or more, get 10% off
            </h2>
            <p className="mt-4 text-[#FAF7F2]/60 leading-relaxed max-w-md">
              Mix and match any of our mixes — vanilla for the purists, chocolate for the
              dreamers. Ten percent off, auto-applied at checkout when your basket holds two or more.
            </p>
            {total !== null && (
              <div className="mt-7">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-baseline gap-3">
                    <span className="font-serif text-xl text-[#FAF7F2]/45 line-through">
                      ₹{total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </span>
                    <span data-testid="bundle-price" className="font-serif text-3xl font-semibold">
                      ₹{bundlePrice.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <span className="rounded-full bg-[#D1E8E2] text-[#2A524A] px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] font-medium">
                    Save 10% · auto-applied at checkout
                  </span>
                </div>
                <button
                  data-testid="add-bundle-button"
                  onClick={() => addItems(["vanilla", "chocolate"])}
                  disabled={busy}
                  className="mt-5 rounded-full bg-[#F8C8DC] text-[#6A2C3E] px-7 py-3.5 text-sm font-semibold hover:bg-[#FAF7F2] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <ShoppingBag className="w-4 h-4" /> Add Both to Basket
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-center">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl rotate-1 spotlight-frame group">
              <img
                src="/assets/bundle-pair.png"
                alt="Almo Bakes vanilla and chocolate canisters tied together with a cream ribbon and a gift tag"
                className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

const CHIPS = [
  { icon: BadgeCheck, label: "Certified Organic Ingredients" },
  { icon: Leaf, label: "No refined sugar" },
  { icon: WheatOff, label: "Gluten free" },
  { icon: FlaskConical, label: "No preservatives" },
  { icon: Timer, label: "5-min prep" },
];

function MixCard({ product, index }) {
  const [tab, setTab] = useState("inside");
  const { products, addItem, busy } = useCart();
  const shop = products[product.id];

  return (
    <Reveal delay={index * 0.12}>
      <div className={`rounded-[2.5rem] ${product.panel} overflow-hidden p-7 sm:p-10 h-full`}>
        <motion.div
          whileHover={{ scale: 1.015, rotate: index % 2 ? 1 : -1 }}
          transition={{ type: "spring", stiffness: 180, damping: 18 }}
          className="rounded-[1.75rem] overflow-hidden shadow-2xl shadow-black/20 spotlight-frame"
        >
          <img
            src={product.boxImg}
            alt={`Almo Bakes ${product.flavor.toLowerCase()} organic almond flour cake mix box`}
            className="w-full aspect-square object-cover"
          />
        </motion.div>

        <div className="mt-8">
          <span data-testid={`product-ribbon-${product.id}`} className={`inline-flex items-center gap-2 rounded-full px-5 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] ${product.ribbon}`}>
            ♥ {product.flavor} ♥
          </span>
          <h3 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2C221E] mt-4 tracking-tight">
            {product.title}
          </h3>
          <p className="mt-3 text-[#2C221E]/70 leading-relaxed">{product.blurb}</p>

          <div className="mt-5 flex flex-wrap gap-2.5">
            {CHIPS.map(({ icon: Icon, label }) => (
              <span key={label} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium ${product.chipBorder}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </span>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap gap-2" role="tablist">
            {[
              ["inside", "What's inside"],
              ["facts", "Good to know"],
              ["nutrition", "Nutrition"],
            ].map(([key, label]) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                data-testid={`product-tab-${key}-${product.id}`}
                onClick={() => setTab(key)}
                className={`rounded-full px-5 py-2 text-xs font-mono uppercase tracking-[0.15em] transition-all duration-300 ${
                  tab === key ? "bg-[#2C221E] text-[#FAF7F2]" : "text-[#2C221E]/60 hover:text-[#2C221E]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5"
            data-testid={`product-panel-${tab}-${product.id}`}
          >
            {tab === "inside" ? (
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {product.ingredients.map((ing) => (
                  <li key={ing} className="flex items-center gap-2.5 text-sm text-[#2C221E]/80">
                    <Check className={`w-4 h-4 shrink-0 ${product.accentText}`} /> {ing}
                  </li>
                ))}
              </ul>
            ) : tab === "nutrition" ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#2C221E]/20">
                      {product.nutrition.columns.map((c, i) => (
                        <th key={c} className={`${i === 0 ? "text-left" : "text-right"} font-mono text-[10px] uppercase tracking-[0.15em] text-[#2C221E]/60 pb-2 ${i > 0 ? "pl-3" : ""}`}>
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {product.nutrition.rows.map((row) => (
                      <tr key={row[0]} className="border-b border-[#2C221E]/8">
                        {row.map((cell, i) => (
                          <td key={i} className={`${i === 0 ? "text-left font-medium text-[#2C221E]" : "text-right text-[#2C221E]/70 pl-3"} py-2 text-xs sm:text-sm`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-[#2C221E]/50 leading-relaxed">
                  {product.nutrition.note}
                </p>
              </div>
            ) : (
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {product.facts.map(([k, v]) => (
                  <div key={k} className="flex flex-col">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#2C221E]/50">{k}</dt>
                    <dd className="text-sm font-medium text-[#2C221E] mt-0.5">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </motion.div>

          <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.25em] text-[#2C221E]/50">
            250 g · Makes one 6″ cake · 10 slices
          </p>

          {shop && (
            <div className="mt-4 flex flex-wrap items-center gap-5">
              <span data-testid={`product-price-${product.id}`} className="font-serif text-3xl font-semibold text-[#2C221E]">
                ₹{Number(shop.price).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </span>
              <button
                data-testid={`add-to-cart-${product.id}`}
                onClick={() => addItem(product.id)}
                disabled={busy}
                className="rounded-full bg-[#2C221E] text-[#FAF7F2] px-7 py-3.5 text-sm font-medium hover:bg-[#6A2C3E] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 inline-flex items-center gap-2 disabled:opacity-50"
              >
                <ShoppingBag className="w-4 h-4" /> Add to Basket
              </button>
            </div>
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function OurMixes() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="pt-[112px]">
      <div className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#F8C8DC]/40 blur-[120px] pointer-events-none" />
      <div className="absolute top-96 -right-32 w-[420px] h-[420px] rounded-full bg-[#D1E8E2]/50 blur-[120px] pointer-events-none" />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-24 relative" data-testid="our-mixes-page">
        <Reveal>
          <Link
            to="/"
            data-testid="mixes-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#6E5E57] hover:text-[#6A2C3E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C86D51] mt-12 mb-4">Our Mixes</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2C221E] leading-[1.08]">
            Two flavors.<br />
            <em className="italic text-[#6A2C3E]">Zero</em> compromise.
          </h1>
          <p className="mt-6 text-[#6E5E57] leading-relaxed max-w-xl">
            Small-batch organic almond flour cake mixes, sweetened with organic coconut sugar and
            made to be baked together — little hands very welcome.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-start" data-testid="products-showcase">
          {PRODUCTS.map((p, i) => (
            <MixCard key={p.id} product={p} index={i} />
          ))}
        </div>

        <BundleCard />
      </section>
    </main>
  );
}
